<script lang="ts">
export type UiToastTone = 'info' | 'success' | 'warning' | 'danger' | 'blocker';
export type UiToastState = 'idle' | 'loading';
export type UiToastRole = 'status' | 'alert';
export type UiToastAriaLive = 'polite' | 'assertive';
</script>

<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import type { Component } from 'vue';
import {
  CircleAlert,
  CircleCheck,
  Info,
  LoaderCircle,
  ShieldAlert,
  TriangleAlert,
  X
} from 'lucide-vue-next';

import UiButton from './UiButton.vue';
import UiIconButton from './UiIconButton.vue';

const props = withDefaults(
  defineProps<{
    tone?: UiToastTone;
    state?: UiToastState;
    title?: string;
    message: string;
    reasons?: string[];
    hiddenReasonCount?: number;
    repeatCount?: number;
    actionLabel?: string;
    actionBusy?: boolean;
    closable?: boolean;
    role: UiToastRole;
    ariaLive: UiToastAriaLive;
    progressDuration?: number;
    progressPaused?: boolean;
    interactiveTabindex?: number;
  }>(),
  {
    tone: 'info',
    state: 'idle',
    title: undefined,
    reasons: () => [],
    hiddenReasonCount: 0,
    repeatCount: 0,
    actionLabel: undefined,
    actionBusy: false,
    closable: true,
    progressDuration: undefined,
    progressPaused: false,
    interactiveTabindex: undefined
  }
);

const emit = defineEmits<{
  action: [];
  close: [];
}>();

const iconByTone: Record<UiToastTone, Component> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
  blocker: ShieldAlert
};
const MAX_VISIBLE_REASON_COUNT = 3;
const reasonsExpanded = shallowRef(false);

const resolvedIcon = computed(() =>
  props.state === 'loading' ? LoaderCircle : iconByTone[props.tone]
);
const visibleReasons = computed(() =>
  reasonsExpanded.value
    ? props.reasons
    : props.reasons.slice(0, MAX_VISIBLE_REASON_COUNT)
);
const totalHiddenReasonCount = computed(() =>
  Math.max(
    props.hiddenReasonCount,
    props.reasons.length - MAX_VISIBLE_REASON_COUNT
  )
);
const resolvedHiddenReasonCount = computed(() =>
  reasonsExpanded.value
    ? 0
    : totalHiddenReasonCount.value
);
const canToggleReasons = computed(
  () => reasonsExpanded.value || props.reasons.length > MAX_VISIBLE_REASON_COUNT
);

const toggleReasons = () => {
  reasonsExpanded.value = !reasonsExpanded.value;
};

const normalizedProgressDuration = computed(() => {
  if (
    props.progressDuration === undefined ||
    !Number.isFinite(props.progressDuration) ||
    props.progressDuration <= 0
  ) {
    return undefined;
  }

  return props.progressDuration;
});

const showProgress = computed(
  () =>
    normalizedProgressDuration.value !== undefined &&
    props.state !== 'loading' &&
    props.tone !== 'blocker'
);

const progressStyle = computed(() => ({
  '--ui-toast-progress-duration': `${normalizedProgressDuration.value ?? 0}ms`,
  '--ui-toast-progress-play-state': props.progressPaused ? 'paused' : 'running'
}));
</script>

<template>
  <article
    class="ui-toast"
    :class="[
      `ui-toast--${tone}`,
      state === 'loading' && 'ui-toast--loading',
      reasonsExpanded && 'ui-toast--reasons-expanded',
      !title && 'ui-toast--without-title'
    ]"
    :role="role"
    :aria-live="ariaLive"
    aria-atomic="true"
    :aria-busy="state === 'loading' || undefined"
  >
    <span
      class="ui-toast__rail"
      aria-hidden="true"
    />

    <span
      class="ui-toast__icon"
      aria-hidden="true"
    >
      <component
        :is="resolvedIcon"
        :size="18"
        :stroke-width="1.9"
      />
    </span>

    <div class="ui-toast__content">
      <div
        v-if="title || repeatCount > 1"
        class="ui-toast__heading"
      >
        <p
          v-if="title"
          class="ui-toast__title"
        >
          {{ title }}
        </p>
        <span
          v-if="repeatCount > 1"
          class="ui-toast__repeat"
          :aria-label="`Количество повторов: ${repeatCount}`"
        >
          ×{{ repeatCount }}
        </span>
      </div>

      <p class="ui-toast__message">
        {{ message }}
      </p>

      <ul
        v-if="visibleReasons.length"
        class="ui-toast__reasons"
        aria-hidden="true"
      >
        <li
          v-for="(reason, index) in visibleReasons"
          :key="`${index}:${reason}`"
        >
          <CircleAlert
            :size="14"
            :stroke-width="1.9"
            aria-hidden="true"
          />
          <span>{{ reason }}</span>
        </li>
      </ul>

      <button
        v-if="canToggleReasons"
        type="button"
        class="ui-toast__hidden-reasons"
        :aria-expanded="reasonsExpanded"
        :tabindex="interactiveTabindex"
        @click="toggleReasons"
      >
        {{
          reasonsExpanded
            ? 'Скрыть причины'
            : `Ещё причин: ${resolvedHiddenReasonCount}`
        }}
      </button>
      <p
        v-else-if="resolvedHiddenReasonCount > 0"
        class="ui-toast__hidden-reasons-count"
      >
        Ещё причин: {{ resolvedHiddenReasonCount }}
      </p>
      <p
        v-if="canToggleReasons"
        class="ui-toast__mobile-hidden-reasons"
      >
        Ещё причин: {{ totalHiddenReasonCount }}
      </p>

      <ul
        v-if="reasons.length"
        class="ui-toast__screen-reader-reasons"
        aria-label="Все причины блокировки"
      >
        <li
          v-for="(reason, index) in reasons"
          :key="`screen-reader:${index}:${reason}`"
        >
          {{ reason }}
        </li>
      </ul>

      <UiButton
        v-if="actionLabel"
        type="button"
        :class="['ui-toast__action', actionBusy && 'ui-toast__action--busy']"
        variant="primary"
        size="sm"
        :icon="actionBusy ? LoaderCircle : undefined"
        :disabled="actionBusy"
        :aria-busy="actionBusy || undefined"
        :tabindex="interactiveTabindex"
        @click="emit('action')"
      >
        {{ actionLabel }}
      </UiButton>
    </div>

    <UiIconButton
      v-if="closable"
      type="button"
      class="ui-toast__close"
      variant="ghost"
      size="md"
      aria-label="Закрыть уведомление"
      :tabindex="interactiveTabindex"
      @click="emit('close')"
    >
      <X
        :size="18"
        :stroke-width="1.9"
        aria-hidden="true"
      />
    </UiIconButton>
    <span
      v-if="showProgress"
      class="ui-toast__progress-track"
      aria-hidden="true"
    >
      <span
        class="ui-toast__progress"
        :style="progressStyle"
      />
    </span>
  </article>
</template>

<style scoped>
.ui-toast {
  --ui-toast-bg: var(--ui-toast-info-bg);
  --ui-toast-border: var(--ui-status-info-border);
  --ui-toast-accent: var(--ui-info);
  --ui-toast-icon-bg: color-mix(in srgb, var(--ui-info) 15%, var(--ui-toast-surface));
  --ui-toast-icon-color: var(--ui-status-info-text);
  --ui-toast-progress-duration: 0ms;
  --ui-toast-progress-play-state: running;

  position: relative;
  box-sizing: border-box;
  display: grid;
  width: min(25rem, 100%);
  grid-template-columns: 2.125rem minmax(0, 1fr) 2.75rem;
  gap: var(--ui-space-3);
  align-items: start;
  overflow: hidden;
  padding: var(--ui-space-4);
  border: 1px solid var(--ui-toast-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-toast-bg);
  box-shadow: var(--ui-shadow-panel);
  color: var(--ui-foreground);
}

.ui-toast__rail {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0.1875rem;
  background: var(--ui-toast-accent);
}

.ui-toast__icon {
  display: inline-flex;
  width: 2.125rem;
  height: 2.125rem;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--ui-toast-accent) 42%, var(--ui-border));
  border-radius: var(--ui-radius-md);
  background: var(--ui-toast-icon-bg);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 32%, transparent);
  color: var(--ui-toast-icon-color);
}

.ui-toast__content {
  display: grid;
  min-width: 0;
  gap: 0.1875rem;
  padding-top: 0.0625rem;
}

.ui-toast__heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-2);
}

.ui-toast__title,
.ui-toast__message,
.ui-toast__hidden-reasons-count,
.ui-toast__mobile-hidden-reasons {
  margin: 0;
  overflow-wrap: anywhere;
}

.ui-toast__screen-reader-reasons {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  padding: 0;
  border: 0;
  margin: -1px;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.ui-toast__title {
  color: var(--ui-foreground);
  font-size: 0.95rem;
  font-weight: 750;
  line-height: 1.35;
}

.ui-toast__message {
  color: var(--ui-foreground-muted);
  font-size: 0.9rem;
  line-height: 1.45;
}

.ui-toast--without-title .ui-toast__message {
  color: var(--ui-foreground);
  font-weight: 680;
}

.ui-toast__repeat {
  display: inline-flex;
  min-width: 1.625rem;
  min-height: 1.625rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--ui-toast-accent) 42%, var(--ui-border));
  border-radius: var(--ui-radius-full);
  background: color-mix(in srgb, var(--ui-toast-accent) 10%, var(--ui-toast-surface));
  color: var(--ui-toast-icon-color);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.2rem 0.45rem;
}

.ui-toast__reasons {
  display: grid;
  gap: 0.4rem;
  margin: var(--ui-space-2) 0 0;
  padding: 0;
  list-style: none;
}

.ui-toast__reasons li {
  display: grid;
  min-width: 0;
  grid-template-columns: 0.875rem minmax(0, 1fr);
  gap: 0.45rem;
  align-items: start;
  color: var(--ui-foreground);
  font-size: 0.84rem;
  font-weight: 620;
  line-height: 1.4;
}

.ui-toast__reasons svg {
  margin-top: 0.15rem;
  color: var(--ui-toast-accent);
}

.ui-toast__reasons span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.ui-toast__hidden-reasons,
.ui-toast__hidden-reasons-count,
.ui-toast__mobile-hidden-reasons {
  width: fit-content;
  margin-top: var(--ui-space-1);
  color: var(--ui-toast-icon-color);
  font-size: 0.8rem;
  font-weight: 760;
  line-height: 1.3;
}

.ui-toast__mobile-hidden-reasons {
  display: none;
}

.ui-toast__hidden-reasons {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, currentColor 52%, transparent);
  text-underline-offset: 0.18em;
}

.ui-toast__hidden-reasons:focus-visible {
  border-radius: var(--ui-radius-sm);
  outline: 2px solid var(--ui-toast-accent);
  outline-offset: 2px;
}

.ui-toast__action {
  width: fit-content;
  max-width: 100%;
  margin-top: var(--ui-space-2);
}

.ui-toast__action--busy :deep(.ui-button__icon svg) {
  animation: ui-toast-spin 0.9s linear infinite;
}

.ui-toast__close {
  flex: 0 0 auto;
  margin: -0.375rem -0.375rem 0 0;
  color: var(--ui-foreground-muted);
}

.ui-toast__progress-track {
  position: absolute;
  inset: auto 0 0;
  height: 0.1875rem;
  overflow: hidden;
  background: color-mix(in srgb, var(--ui-toast-accent) 16%, var(--ui-toast-bg));
}

.ui-toast__progress {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--ui-toast-accent);
  transform: scaleX(1);
  transform-origin: left center;
  animation: ui-toast-progress var(--ui-toast-progress-duration) linear forwards;
  animation-play-state: var(--ui-toast-progress-play-state);
}

.ui-toast--success {
  --ui-toast-bg: var(--ui-toast-success-bg);
  --ui-toast-border: var(--ui-status-success-border);
  --ui-toast-accent: var(--ui-success);
  --ui-toast-icon-bg: color-mix(in srgb, var(--ui-success) 15%, var(--ui-toast-surface));
  --ui-toast-icon-color: var(--ui-status-success-text);
}

.ui-toast--warning {
  --ui-toast-bg: var(--ui-toast-warning-bg);
  --ui-toast-border: var(--ui-status-warning-border);
  --ui-toast-accent: var(--ui-warning);
  --ui-toast-icon-bg: color-mix(in srgb, var(--ui-warning) 16%, var(--ui-toast-surface));
  --ui-toast-icon-color: var(--ui-status-warning-text);
}

.ui-toast--danger {
  --ui-toast-bg: var(--ui-toast-danger-bg);
  --ui-toast-border: var(--ui-status-danger-border);
  --ui-toast-accent: var(--ui-danger);
  --ui-toast-icon-bg: color-mix(in srgb, var(--ui-danger) 14%, var(--ui-toast-surface));
  --ui-toast-icon-color: var(--ui-status-danger-text);
}

.ui-toast--blocker {
  --ui-toast-bg: var(--ui-toast-blocker-bg);
  --ui-toast-border: var(--ui-status-danger-border);
  --ui-toast-accent: var(--ui-danger);
  --ui-toast-icon-bg: color-mix(in srgb, var(--ui-danger) 14%, var(--ui-toast-surface));
  --ui-toast-icon-color: var(--ui-status-danger-text);
}

.ui-toast--loading .ui-toast__icon svg {
  animation: ui-toast-spin 0.9s linear infinite;
}

@keyframes ui-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ui-toast-progress {
  to {
    transform: scaleX(0);
  }
}

@media (max-width: 640px) {
  .ui-toast {
    grid-template-columns: 1.875rem minmax(0, 1fr) 2.75rem;
    gap: 0.625rem;
    padding: var(--ui-space-3) 0.625rem var(--ui-space-3) var(--ui-space-3);
    border-radius: var(--ui-radius-md);
  }

  .ui-toast__icon {
    width: 1.875rem;
    height: 1.875rem;
  }

  .ui-toast__title {
    font-size: 0.9rem;
  }

  .ui-toast__message {
    display: -webkit-box;
    overflow: hidden;
    font-size: 0.84rem;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .ui-toast__title {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .ui-toast__reasons {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    padding: 0;
    border: 0;
    margin: -1px;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .ui-toast__hidden-reasons {
    display: none;
  }

  .ui-toast__mobile-hidden-reasons {
    display: block;
  }

  .ui-toast__action {
    margin-top: 0.375rem;
  }

  .ui-toast__close {
    margin-top: -0.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ui-toast__progress-track {
    display: none;
  }

  .ui-toast__action--busy :deep(.ui-button__icon svg),
  .ui-toast--loading .ui-toast__icon svg {
    animation: none;
  }
}
</style>
